import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import logo from "../logo.svg";

const baseMenuItems = [
  {
    name: "ISO Actionables At A Glance",
    description:
      "Review upcoming, pending, and completed work in one operational view.",
    route: "/select-policy",
  },
  {
    name: "ISO System Trigger Register",
    description: "Monitor internally triggered compliance work with a clear record.",
    route: "/system-trigger",
  },
  {
    name: "ISO External Trigger Register",
    description: "Track externally triggered items and associated deadlines.",
    route: "/external-trigger",
  },
  {
    name: "Actionables Tracker",
    description: "Follow the current transaction register and status movement.",
    route: "/actionables",
  },
  {
    name: "ISO Compliance Report",
    description: "Generate policy-wise compliance visibility and reporting snapshots.",
    route: "/reports",
  },
  {
    name: "Actionables Calendar",
    description: "View the execution schedule in a clean, date-based layout.",
    route: "/actionables-calendar",
  },
];

const headMenus = [
  {
    name: "ISO Mandated Meetings",
    description: "Organize meetings, invite teams, and review past sessions.",
    route: "/policy-review",
  },
  {
    name: "ISO Policy Review",
    description: "Run structured review cycles for policies and documents.",
    route: "/policy-reviews",
  },
  {
    name: "View Exception Reports",
    description: "Inspect reported exceptions and mitigation actions in one place.",
    route: "/exception-reports",
  },
];

const Dashboard = () => {
  const [username, setUsername] = useState("");
  const [systemRole, setSystemRole] = useState("");
  const navigate = useNavigate();

  useEffect(() => {
    const accessToken = localStorage.getItem("accessToken");
    if (!accessToken) return;

    axios
      .get("/api/users/me/", {
        headers: { Authorization: `Bearer ${accessToken}` },
      })
      .then((res) => {
        setUsername(res.data.username || "User");
        setSystemRole(res.data.system_role || "");
      })
      .catch((err) => {
        console.error("Error fetching user:", err);
        setUsername("User");
      });
  }, []);

  const menuItems =
    systemRole === "Head" ? [...baseMenuItems, ...headMenus] : baseMenuItems;

  return (
    <div className="page-shell">
      <section className="hero-banner">
        <div
          style={{
            display: "flex",
            flexWrap: "wrap",
            gap: "20px",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <div>
            <div
              style={{
                display: "inline-flex",
                padding: "8px 14px",
                borderRadius: "999px",
                background: "rgba(255,255,255,0.12)",
                border: "1px solid rgba(255,255,255,0.16)",
                fontSize: "0.78rem",
                fontWeight: 700,
                letterSpacing: "0.14em",
                textTransform: "uppercase",
              }}
            >
              Dashboard
            </div>
            <h1 style={{ marginTop: "18px" }}>Welcome, {username || "User"}</h1>
            <p>
              Your ISO operations workspace now brings policies, actionables,
              reports, meetings, and exception workflows into one more polished
              experience.
            </p>
          </div>

          <div
            style={{
              display: "grid",
              gap: "14px",
              justifyItems: "center",
              minWidth: "180px",
            }}
          >
            <img
              src={logo}
              alt="Company Logo"
              style={{
                width: "94px",
                height: "94px",
                padding: "16px",
                borderRadius: "24px",
                background: "rgba(255,255,255,0.1)",
                border: "1px solid rgba(255,255,255,0.14)",
              }}
            />
            <div
              className="pill"
              style={{
                color: "#fff",
                background: "rgba(255,255,255,0.12)",
                borderColor: "rgba(255,255,255,0.16)",
              }}
            >
              Role: {systemRole || "User"}
            </div>
          </div>
        </div>
      </section>

      <section
        className="metric-grid"
        style={{
          gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
          marginBottom: "1.4rem",
        }}
      >
        {[
          { label: "Modules", value: menuItems.length },
          { label: "User Role", value: systemRole || "Standard" },
          { label: "Workspace", value: "Active" },
        ].map((item) => (
          <div
            key={item.label}
            className="surface-card"
            style={{ padding: "1.25rem" }}
          >
            <div
              style={{
                fontSize: "0.82rem",
                color: "#65748f",
                fontWeight: 700,
              }}
            >
              {item.label}
            </div>
            <div
              style={{
                marginTop: "8px",
                fontSize: "1.4rem",
                fontWeight: 800,
                color: "#102a71",
              }}
            >
              {item.value}
            </div>
          </div>
        ))}
      </section>

      <section className="metric-grid">
        {menuItems.map((item) => (
          <button
            key={item.name}
            type="button"
            className="surface-card"
            onClick={() => navigate(item.route)}
            style={{
              padding: "1.5rem",
              textAlign: "left",
              border: "1px solid rgba(255,255,255,0.12)",
              transition:
                "transform 0.2s ease, box-shadow 0.2s ease, border-color 0.2s ease",
              background: "linear-gradient(135deg, #0f1d60, #102a71)",
              color: "#fff",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = "translateY(-4px)";
              e.currentTarget.style.boxShadow =
                "0 26px 44px rgba(15,29,96,0.4)";
              e.currentTarget.style.borderColor =
                "rgba(255,255,255,0.25)";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = "translateY(0)";
              e.currentTarget.style.boxShadow = "";
              e.currentTarget.style.borderColor =
                "rgba(255,255,255,0.12)";
            }}
          >
            <div
              style={{
                display: "inline-flex",
                marginBottom: "16px",
                padding: "8px 12px",
                borderRadius: "999px",
                background: "rgba(255,255,255,0.12)",
                color: "#ffffff",
                border: "1px solid rgba(255,255,255,0.18)",
                fontSize: "0.76rem",
                fontWeight: 800,
                letterSpacing: "0.12em",
                textTransform: "uppercase",
              }}
            >
              Open Module
            </div>

            <h2
              style={{
                margin: 0,
                fontSize: "1.22rem",
                color: "#ffffff",
              }}
            >
              {item.name}
            </h2>

            <p
              style={{
                margin: "12px 0 0",
                color: "rgba(255,255,255,0.75)",
                lineHeight: 1.75,
              }}
            >
              {item.description}
            </p>
          </button>
        ))}
      </section>
    </div>
  );
};

export default Dashboard;